import { useEffect, useMemo, useState } from 'react'

export type PeriodLabel = 'week' | 'month' | 'quarter' | 'year' | 'custom'
export type PeriodBucket = 'week' | 'month'

export interface PeriodRange {
  readonly period: PeriodLabel
  readonly bucket: PeriodBucket
  readonly from: string
  readonly to: string
}

export const PERIOD_OPTIONS: readonly { readonly value: PeriodLabel; readonly label: string }[] = [
  { value: 'week', label: 'Week' },
  { value: 'month', label: 'Month' },
  { value: 'quarter', label: 'Quarter' },
  { value: 'year', label: 'Year' },
  { value: 'custom', label: 'Custom' },
]

const MS_PER_DAY = 86_400_000

function atLocalMidnight(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

function shiftLocalDays(date: Date, days: number): Date {
  return new Date(date.getTime() + days * MS_PER_DAY)
}

function startOfWeek(date: Date): Date {
  const local = atLocalMidnight(date)
  const mondayOffset = (local.getDay() + 6) % 7
  return shiftLocalDays(local, -mondayOffset)
}

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

function startOfQuarter(date: Date): Date {
  const quarterMonth = Math.floor(date.getMonth() / 3) * 3
  return new Date(date.getFullYear(), quarterMonth, 1)
}

function startOfYear(date: Date): Date {
  return new Date(date.getFullYear(), 0, 1)
}

function nextMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 1)
}

function nextQuarter(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth() + 3, 1)
}

function nextYear(date: Date): Date {
  return new Date(date.getFullYear() + 1, 0, 1)
}

export function bucketForPeriod(period: PeriodLabel): PeriodBucket {
  return period === 'year' ? 'month' : 'week'
}

export function bucketForRange(fromIso: string, toIso: string): PeriodBucket {
  const from = Date.parse(fromIso)
  const to = Date.parse(toIso)
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return 'week'
  return to - from <= 31 * MS_PER_DAY ? 'week' : 'month'
}

export function resolvePeriod(period: PeriodLabel, today: Date = new Date()): PeriodRange {
  const start =
    period === 'week'
      ? startOfWeek(today)
      : period === 'month'
        ? startOfMonth(today)
        : period === 'quarter'
          ? startOfQuarter(today)
          : startOfYear(today)
  const end =
    period === 'week'
      ? shiftLocalDays(start, 7)
      : period === 'month'
        ? nextMonth(start)
        : period === 'quarter'
          ? nextQuarter(start)
          : nextYear(start)
  return {
    period,
    bucket: bucketForPeriod(period),
    from: start.toISOString(),
    to: end.toISOString(),
  }
}

export function usePeriod(initialPeriod: PeriodLabel = 'week') {
  const [period, setPeriod] = useState<PeriodLabel>(initialPeriod)
  const [customFrom, setCustomFrom] = useState('')
  const [customTo, setCustomTo] = useState('')
  const [lastValidRange, setLastValidRange] = useState<PeriodRange>(
    initialPeriod === 'custom' ? resolvePeriod('week') : resolvePeriod(initialPeriod),
  )
  const customRange = useMemo<PeriodRange | null>(() => {
    if (!isValidRange(customFrom, customTo)) return null
    return {
      period: 'custom',
      bucket: bucketForRange(customFrom, customTo),
      from: customFrom,
      to: customTo,
    }
  }, [customFrom, customTo])
  const range = period === 'custom' ? customRange ?? lastValidRange : resolvePeriod(period)

  useEffect(() => {
    if (period === 'custom') {
      if (customRange !== null) setLastValidRange(customRange)
      return
    }
    setLastValidRange(resolvePeriod(period))
  }, [customRange, period])

  return {
    period,
    setPeriod,
    bucket: range.bucket,
    from: range.from,
    to: range.to,
    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,
    isValidRange: period !== 'custom' ? true : isValidRange(customFrom, customTo),
  }
}

function isValidRange(fromIso: string, toIso: string): boolean {
  return fromIso !== '' && toIso !== '' && Date.parse(toIso) > Date.parse(fromIso)
}

/**
 * `<input type="date">` speaks `YYYY-MM-DD`; the analytics API speaks ISO instants. These
 * two live here rather than in a screen because both the Analytics page and the person
 * page render a custom range and must agree on the conversion.
 */
export function toInputDate(iso: string): string {
  return iso.slice(0, 10)
}

export function fromInputDate(value: string): string {
  if (value === '') return ''
  return new Date(`${value}T00:00:00.000Z`).toISOString()
}
